import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tooltip,
  Select,
  Chip,
  ListSubheader,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Divider
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HikingRoundedIcon from '@mui/icons-material/HikingRounded';
import FlashOnRoundedIcon from '@mui/icons-material/FlashOnRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import SentimentSatisfiedAltRoundedIcon from '@mui/icons-material/SentimentSatisfiedAltRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import { PageHeader } from '../components/ui';
import { trekAPI, adminAPI, crmAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { REGISTRATION_STATUS_OPTIONS } from '../constants/registrationStatuses';
import './WhatsAppAutomation.css';

const DEFAULT_TRIGGERS = ['pdf', 'callback', 'manual'];

const FORMATTED_REGISTRATION_STATUS_OPTIONS = REGISTRATION_STATUS_OPTIONS.map((opt) => ({
  value: opt.value,
  label: String(opt.label || opt.value).replace(/_/g, ' ')
}));

const filterSelectMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 280,
      width: 260,
      borderRadius: 10,
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
    }
  }
};

function getCategoryLabel(category) {
  if (!category) return 'General';
  if (typeof category === 'string') return category;
  if (typeof category === 'object' && category?.name) return category.name;
  return 'General';
}

// Parses WhatsApp style *bold* text and dynamic parameters
function renderWhatsAppFormattedText(body, trekName = 'Trek', recipientName = 'Participant') {
  if (!body) return null;
  const text = String(body)
    .replace(/{{1}}/g, recipientName)
    .replace(/{{2}}/g, trekName)
    .replace(/{{name}}/gi, recipientName)
    .replace(/{{trek}}/gi, trekName);

  const lines = text.split('\n');
  return lines.map((line, lIdx) => {
    const boldRegex = /\*([^*]+)\*/g;
    const segments = [];
    let cur = 0;
    let match;
    while ((match = boldRegex.exec(line)) !== null) {
      if (match.index > cur) {
        segments.push({ text: line.substring(cur, match.index), bold: false });
      }
      segments.push({ text: match[1], bold: true });
      cur = match.index + match[0].length;
    }
    if (cur < line.length) {
      segments.push({ text: line.substring(cur), bold: false });
    }

    return (
      <React.Fragment key={lIdx}>
        {segments.map((seg, sIdx) =>
          seg.bold ? (
            <strong key={sIdx} style={{ fontWeight: 700, color: '#0f172a' }}>
              {seg.text}
            </strong>
          ) : (
            <span key={sIdx}>{seg.text}</span>
          )
        )}
        {lIdx < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
}

// Normalize a list of template items
function normalizeTemplateArray(raw) {
  if (!raw) return [];
  let list = raw;
  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      const id = String(item?.id || item?.templateId || item?.leadTemplateId || '').trim();
      if (!id) return null;
      const isCarousel = Boolean(
        item?.isCarousel ||
        item?.is_carousel ||
        String(item?.headerFormat || item?.header_format || '').toUpperCase() === 'CAROUSEL' ||
        String(item?.mediaType || item?.media_type || '').toLowerCase() === 'carousel' ||
        (Array.isArray(item?.carouselCards) && item.carouselCards.length > 0) ||
        (Array.isArray(item?.carousel_cards) && item.carousel_cards.length > 0)
      );
      const carouselCards = Array.isArray(item?.carouselCards)
        ? item.carouselCards
        : (Array.isArray(item?.carousel_cards) ? item.carousel_cards : []);
      const carouselCardCount = item?.carouselCardCount || item?.carousel_card_count || carouselCards.length || 0;
      const carouselData = Array.isArray(item?.carouselData)
        ? item.carouselData
        : (Array.isArray(item?.carousel_data) ? item.carousel_data : []);

      return {
        id,
        name: String(item?.name || item?.templateName || id).trim(),
        language: String(item?.language || 'en_US').trim(),
        headerFormat: isCarousel ? 'CAROUSEL' : (String(item?.headerFormat || item?.header_format || item?.mediaType || '').toUpperCase() || null),
        mediaType: isCarousel ? 'carousel' : (String(item?.mediaType || item?.media_type || '').toLowerCase() || null),
        mediaUrl: String(item?.mediaUrl || item?.media_url || item?.templateMediaUrl || '').trim() || null,
        sampleMediaUrl: String(item?.sampleMediaUrl || item?.sample_media_url || '').trim() || null,
        body: String(item?.body || item?.message || '').trim() || null,
        requiresMedia: Boolean(item?.requiresMedia || item?.mediaUrl || item?.templateMediaUrl || isCarousel),
        triggers: Array.isArray(item?.triggers) && item.triggers.length > 0 ? item.triggers : [...DEFAULT_TRIGGERS],
        enabled: item?.enabled !== false,
        isCarousel,
        carouselCards,
        carouselCardCount,
        carouselData
      };
    })
    .filter(Boolean);
}

// Parses raw DB json (handles legacy formats & new multi-sequence structure + lead status follow-up automations)
function parseTrekAutomations(raw) {
  const defaults = {
    sequence1_lead: {
      enabled: true,
      title: 'On Lead Creation',
      description: 'Dispatches when a participant creates an inquiry via Get PDF, Callback Request, or Manual CRM Lead.',
      templates: []
    },
    sequence2_booking: {
      enabled: true,
      title: 'On Payment / Booking Confirmed',
      description: 'Dispatches when participant booking status is updated to Confirmed or Advance Paid.',
      templates: []
    },
    custom_sequences: [],
    lead_status_automations: {
      follow_up_done: {
        enabled: true,
        title: 'Follow Up Done',
        description: 'Triggered in CRM when lead status is changed to Follow Up Done.',
        templates: []
      },
      second_follow_up_done: {
        enabled: true,
        title: '2nd Follow Up Done',
        description: 'Triggered in CRM when lead status is changed to 2nd Follow Up Done.',
        templates: []
      },
      third_follow_up_done: {
        enabled: true,
        title: '3rd Follow Up Done',
        description: 'Triggered in CRM when lead status is changed to 3rd Follow Up Done.',
        templates: []
      }
    }
  };

  if (!raw) return defaults;
  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return defaults;
    }
  }
  if (!data || typeof data !== 'object') return defaults;

  const rawStatus = data.lead_status_automations || {};
  const parseStatusItem = (key, defaultTitle, defaultDesc) => {
    const item = rawStatus[key] || data[key] || {};
    const rawTemplates = Array.isArray(item) ? item : (item.templates || []);
    return {
      enabled: item.enabled !== false,
      title: item.title || defaultTitle,
      description: item.description || defaultDesc,
      templates: normalizeTemplateArray(rawTemplates)
    };
  };

  const parsedStatusAutomations = {
    follow_up_done: parseStatusItem('follow_up_done', 'Follow Up Done', 'Triggered in CRM when lead status is changed to Follow Up Done.'),
    second_follow_up_done: parseStatusItem('second_follow_up_done', '2nd Follow Up Done', 'Triggered in CRM when lead status is changed to 2nd Follow Up Done.'),
    third_follow_up_done: parseStatusItem('third_follow_up_done', '3rd Follow Up Done', 'Triggered in CRM when lead status is changed to 3rd Follow Up Done.')
  };

  // If already structured with multi-sequences or status automations
  if (data.sequence1_lead || data.sequence2_booking || Array.isArray(data.custom_sequences) || data.lead_status_automations) {
    return {
      sequence1_lead: {
        ...defaults.sequence1_lead,
        ...data.sequence1_lead,
        templates: normalizeTemplateArray(data.sequence1_lead?.templates || [])
      },
      sequence2_booking: {
        ...defaults.sequence2_booking,
        ...data.sequence2_booking,
        templates: normalizeTemplateArray(data.sequence2_booking?.templates || [])
      },
      custom_sequences: Array.isArray(data.custom_sequences)
        ? data.custom_sequences.map((cs, idx) => ({
            id: cs.id || `custom_seq_${Date.now()}_${idx}`,
            title: cs.title || `Sequence ${idx + 3}: Event Milestone`,
            enabled: cs.enabled !== false,
            daysOffset: Number(cs.daysOffset ?? cs.days ?? 2),
            timing: cs.timing === 'after' ? 'after' : 'before',
            referenceDate: cs.referenceDate || 'event_start_date',
            bookingStatuses: Array.isArray(cs.bookingStatuses)
              ? cs.bookingStatuses
              : (cs.bookingStatus ? [cs.bookingStatus] : ['confirmed', 'advanced_paid']),
            packageIds: Array.isArray(cs.packageIds)
              ? cs.packageIds.map(String)
              : (cs.packageId ? [String(cs.packageId)] : []),
            stayOptionIds: Array.isArray(cs.stayOptionIds)
              ? cs.stayOptionIds.map(String)
              : (cs.stayOptionId ? [String(cs.stayOptionId)] : []),
            templates: normalizeTemplateArray(cs.templates || [])
          }))
        : [],
      lead_status_automations: parsedStatusAutomations
    };
  }

  // Legacy single object format (e.g. { default, callback, pdf } or { templates: [...] })
  // Automatically maps existing live configurations (like Dev Hatra) into Sequence 1!
  let legacyTemplates = [];
  if (Array.isArray(data)) {
    legacyTemplates = normalizeTemplateArray(data);
  } else if (Array.isArray(data?.templates)) {
    legacyTemplates = normalizeTemplateArray(data.templates);
  } else {
    const legacyItem = data.default || data.callback || data.pdf || (data.id || data.leadTemplateId ? data : null);
    if (legacyItem && (legacyItem.id || legacyItem.leadTemplateId || legacyItem.templateId)) {
      const id = String(legacyItem.id || legacyItem.leadTemplateId || legacyItem.templateId).trim();
      legacyTemplates = [
        {
          id,
          name: String(legacyItem.name || legacyItem.templateName || id).trim(),
          language: String(legacyItem.language || 'en_US').trim(),
          headerFormat: String(legacyItem.headerFormat || legacyItem.header_format || legacyItem.mediaType || '').toUpperCase() || null,
          mediaType: String(legacyItem.mediaType || '').toLowerCase() || null,
          mediaUrl: String(legacyItem.mediaUrl || legacyItem.templateMediaUrl || legacyItem.media_url || '').trim() || null,
          sampleMediaUrl: String(legacyItem.sampleMediaUrl || '').trim() || null,
          body: String(legacyItem.body || '').trim() || null,
          requiresMedia: Boolean(legacyItem.requiresMedia || legacyItem.mediaUrl || legacyItem.templateMediaUrl),
          triggers: [...DEFAULT_TRIGGERS],
          enabled: true
        }
      ];
    }
  }

  return {
    ...defaults,
    sequence1_lead: {
      ...defaults.sequence1_lead,
      templates: legacyTemplates
    },
    lead_status_automations: parsedStatusAutomations
  };
}

function countTotalConfigured(trek) {
  const automations = parseTrekAutomations(trek?.leadTemplates ?? trek?.lead_templates);
  const s1Count = automations.sequence1_lead?.templates?.filter((t) => t.enabled)?.length || 0;
  const s2Count = automations.sequence2_booking?.templates?.filter((t) => t.enabled)?.length || 0;
  const customCount = automations.custom_sequences?.reduce((sum, cs) => sum + (cs.enabled ? (cs.templates?.filter((t) => t.enabled)?.length || 0) : 0), 0) || 0;
  const statusCount = ['follow_up_done', 'second_follow_up_done', 'third_follow_up_done'].reduce(
    (sum, k) => sum + (automations.lead_status_automations?.[k]?.enabled ? (automations.lead_status_automations?.[k]?.templates?.filter((t) => t.enabled)?.length || 0) : 0),
    0
  );
  return s1Count + s2Count + customCount + statusCount;
}

export default function WhatsAppAutomation() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { can } = useAuth();
  const canManage = can('whatsapp_automation_manage') || can('*');

  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTrek, setSelectedTrek] = useState(null);
  const [automations, setAutomations] = useState(parseTrekAutomations(null));
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingState, setUploadingState] = useState(null);

  // Accordion state: Template 1 in each sequence & follow-up trigger is open by default
  const [expandedAccordions, setExpandedAccordions] = useState({
    'sequence1_lead_0': true,
    'sequence2_booking_0': true,
    'custom_0_0': true,
    'status_follow_up_done_0': true,
    'status_second_follow_up_done_0': true,
    'status_third_follow_up_done_0': true
  });

  // Active sequence for live phone preview mockup
  const [activePreviewSeq, setActivePreviewSeq] = useState('sequence1_lead');

  // Modals
  const [testSendOpen, setTestSendOpen] = useState(false);
  const [testSequenceKey, setTestSequenceKey] = useState('sequence1_lead');
  const [testPhone, setTestPhone] = useState('');
  const [testName, setTestName] = useState('Rahul Sharma');
  const [testSending, setTestSending] = useState(false);

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [targetTrekIds, setTargetTrekIds] = useState([]);
  const [copySearch, setCopySearch] = useState('');
  const [copying, setCopying] = useState(false);

  // 1. Fetch all treks
  const { data: treksRes, isLoading: treksLoading } = useQuery(
    ['wa-treks-list'],
    () => trekAPI.list({ limit: 300 }),
    { staleTime: 60 * 1000 }
  );
  const treks = useMemo(() => {
    const list = treksRes?.data?.data || [];
    return Array.isArray(list) ? list : [];
  }, [treksRes]);

  // 2. Fetch Waplify Templates
  const {
    data: waplifyRes,
    refetch: refetchTemplates
  } = useQuery(
    ['waplify-templates'],
    () => adminAPI.getWaplifyTemplates(),
    { staleTime: 5 * 60 * 1000 }
  );
  const waplifyTemplates = useMemo(() => {
    const list = Array.isArray(waplifyRes?.data?.data) ? waplifyRes.data.data : [];
    return list
      .map((row) => {
        const id = String(row?.id || '').trim();
        if (!id) return null;
        const isCarousel = Boolean(
          row?.isCarousel ||
          row?.is_carousel ||
          String(row?.headerFormat || row?.header_format || '').toUpperCase() === 'CAROUSEL' ||
          String(row?.mediaType || row?.media_type || '').toLowerCase() === 'carousel' ||
          (Array.isArray(row?.carouselCards) && row.carouselCards.length > 0) ||
          (Array.isArray(row?.carousel_cards) && row.carousel_cards.length > 0)
        );
        const carouselCards = Array.isArray(row?.carouselCards)
          ? row.carouselCards
          : (Array.isArray(row?.carousel_cards) ? row.carousel_cards : []);
        const carouselCardCount = row?.carouselCardCount || row?.carousel_card_count || carouselCards.length || 0;

        return {
          id,
          name: String(row?.name || id).trim() || id,
          language: String(row?.language || 'en_US').trim() || 'en_US',
          mediaType: isCarousel ? 'carousel' : (String(row?.mediaType || row?.media_type || '').trim().toLowerCase() || null),
          headerFormat: isCarousel ? 'CAROUSEL' : (String(row?.headerFormat || row?.header_format || row?.mediaType || '').trim().toUpperCase() || null),
          sampleMediaUrl: String(row?.sampleMediaUrl || row?.sample_media_url || '').trim() || null,
          requiresMedia: Boolean(row?.requiresMedia || row?.requires_media || isCarousel),
          body: String(row?.body || row?.message || '').trim() || null,
          isCarousel,
          carouselCards,
          carouselCardCount
        };
      })
      .filter(Boolean);
  }, [waplifyRes]);

  const templateMap = useMemo(() => {
    return new Map(waplifyTemplates.map((t) => [t.id, t]));
  }, [waplifyTemplates]);

  // Restore selected trek from URL
  useEffect(() => {
    const paramId = searchParams.get('trekId');
    if (paramId && treks.length > 0) {
      const found = treks.find((t) => String(t.id) === String(paramId));
      if (found) {
        setSelectedTrek(found);
      }
    }
  }, [searchParams, treks]);

  // When selectedTrek changes, load its automations and ensure Template 1 is open
  useEffect(() => {
    if (selectedTrek) {
      const raw = selectedTrek.leadTemplates ?? selectedTrek.lead_templates;
      const parsed = parseTrekAutomations(raw);
      setAutomations(parsed);
      setExpandedAccordions((prev) => ({
        ...prev,
        'sequence1_lead_0': true,
        'sequence2_booking_0': true,
        'custom_0_0': true
      }));
      setIsDirty(false);
    } else {
      setAutomations(parseTrekAutomations(null));
      setIsDirty(false);
    }
  }, [selectedTrek]);

  // Fresh full trek details from server
  const { data: trekDetailRes } = useQuery(
    ['wa-trek-detail', selectedTrek?.id],
    () => trekAPI.get(selectedTrek.id),
    {
      enabled: Boolean(selectedTrek?.id),
      staleTime: 10 * 1000,
      onSuccess: (res) => {
        const full = res?.data?.data;
        if (full) {
          const raw = full.leadTemplates ?? full.lead_templates;
          const parsed = parseTrekAutomations(raw);
          const hasAny =
            parsed.sequence1_lead?.templates?.length > 0 ||
            parsed.sequence2_booking?.templates?.length > 0 ||
            parsed.custom_sequences?.length > 0;
          if (hasAny) {
            setAutomations(parsed);
          }
        }
      }
    }
  );

  const activeTrekDetail = trekDetailRes?.data?.data || selectedTrek;

  const availablePackages = useMemo(() => {
    const list = activeTrekDetail?.packages || selectedTrek?.packages || [];
    return Array.isArray(list) ? list : [];
  }, [activeTrekDetail, selectedTrek]);

  const availableStayOptions = useMemo(() => {
    const raw =
      activeTrekDetail?.stayOptions ??
      activeTrekDetail?.stay_options ??
      selectedTrek?.stayOptions ??
      selectedTrek?.stay_options;
    if (Array.isArray(raw)) return raw.filter((s) => s && (s.id || s.name));
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((s) => s && (s.id || s.name)) : [];
      } catch {
        return [];
      }
    }
    return [];
  }, [activeTrekDetail, selectedTrek]);

  const handleSelectTrek = (trek) => {
    if (isDirty) {
      if (!window.confirm('You have unsaved changes for the current trek. Switch anyway?')) {
        return;
      }
    }
    setSelectedTrek(trek);
    if (trek) {
      setSearchParams({ trekId: String(trek.id) });
    } else {
      setSearchParams({});
    }
  };

  const toggleAccordion = (key) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // --- Handlers ---
  const handleToggleSequenceEnabled = (seqKey, customIdx = null) => {
    setAutomations((prev) => {
      const copy = { ...prev };
      if (customIdx !== null) {
        const list = [...copy.custom_sequences];
        list[customIdx] = { ...list[customIdx], enabled: !list[customIdx].enabled };
        copy.custom_sequences = list;
      } else if (seqKey?.startsWith('status_')) {
        const statusName = seqKey.replace('status_', '');
        copy.lead_status_automations = {
          ...copy.lead_status_automations,
          [statusName]: {
            ...copy.lead_status_automations?.[statusName],
            enabled: !copy.lead_status_automations?.[statusName]?.enabled
          }
        };
      } else {
        copy[seqKey] = { ...copy[seqKey], enabled: !copy[seqKey].enabled };
      }
      return copy;
    });
    setIsDirty(true);
  };

  const updateSequenceTemplates = (seqKey, customIdx, updateListFn) => {
    setAutomations((prev) => {
      const copy = { ...prev };
      if (customIdx !== null) {
        const list = [...copy.custom_sequences];
        list[customIdx] = { ...list[customIdx], templates: updateListFn(list[customIdx].templates || []) };
        copy.custom_sequences = list;
      } else if (seqKey?.startsWith('status_')) {
        const statusName = seqKey.replace('status_', '');
        copy.lead_status_automations = {
          ...copy.lead_status_automations,
          [statusName]: {
            ...copy.lead_status_automations?.[statusName],
            templates: updateListFn(copy.lead_status_automations?.[statusName]?.templates || [])
          }
        };
      } else {
        copy[seqKey] = { ...copy[seqKey], templates: updateListFn(copy[seqKey].templates || []) };
      }
      return copy;
    });
    setIsDirty(true);
  };

  const handleAddTemplateToSequence = (seqKey, customIdx = null) => {
    if (waplifyTemplates.length === 0) {
      showToast({ message: 'No Waplify templates available to add.', severity: 'warning' });
      return;
    }
    const defaultTpl = waplifyTemplates[0];
    const isCarousel = Boolean(defaultTpl.isCarousel);
    const newTpl = {
      id: defaultTpl.id,
      name: defaultTpl.name,
      language: defaultTpl.language,
      headerFormat: defaultTpl.headerFormat,
      mediaType: defaultTpl.mediaType,
      mediaUrl: null,
      sampleMediaUrl: defaultTpl.sampleMediaUrl,
      body: defaultTpl.body,
      requiresMedia: Boolean(defaultTpl.requiresMedia),
      triggers: [...DEFAULT_TRIGGERS],
      enabled: true,
      isCarousel,
      carouselCards: defaultTpl.carouselCards || [],
      carouselCardCount: defaultTpl.carouselCardCount || 0,
      carouselData: (defaultTpl.carouselCards || []).map((c) => ({ mediaUrl: c.media_url || c.mediaUrl || '' }))
    };

    setAutomations((prev) => {
      const copy = { ...prev };
      if (customIdx !== null) {
        const list = [...copy.custom_sequences];
        const newIdx = (list[customIdx].templates || []).length;
        list[customIdx] = {
          ...list[customIdx],
          templates: [...(list[customIdx].templates || []), newTpl]
        };
        copy.custom_sequences = list;
        setExpandedAccordions((acc) => ({ ...acc, [`custom_${customIdx}_${newIdx}`]: true }));
      } else if (seqKey?.startsWith('status_')) {
        const statusName = seqKey.replace('status_', '');
        const currentList = copy.lead_status_automations?.[statusName]?.templates || [];
        const newIdx = currentList.length;
        copy.lead_status_automations = {
          ...copy.lead_status_automations,
          [statusName]: {
            ...copy.lead_status_automations?.[statusName],
            templates: [...currentList, newTpl]
          }
        };
        setExpandedAccordions((acc) => ({ ...acc, [`${seqKey}_${newIdx}`]: true }));
      } else {
        const newIdx = (copy[seqKey].templates || []).length;
        copy[seqKey] = {
          ...copy[seqKey],
          templates: [...(copy[seqKey].templates || []), newTpl]
        };
        setExpandedAccordions((acc) => ({ ...acc, [`${seqKey}_${newIdx}`]: true }));
      }
      return copy;
    });
    setActivePreviewSeq(customIdx !== null ? `custom_${customIdx}` : seqKey);
    setIsDirty(true);
  };

  const handleChangeTemplateSelection = (seqKey, customIdx, tplIndex, newTemplateId) => {
    const meta = templateMap.get(newTemplateId);
    if (!meta) return;

    updateSequenceTemplates(seqKey, customIdx, (templates) => {
      const updated = [...templates];
      const isCarousel = Boolean(meta.isCarousel);
      const newCarouselCards = meta.carouselCards || [];
      const prevCarouselData = updated[tplIndex]?.carouselData || [];
      const initializedCarouselData = newCarouselCards.map((c, i) => {
        return prevCarouselData[i]?.mediaUrl
          ? prevCarouselData[i]
          : { mediaUrl: c.media_url || c.mediaUrl || '' };
      });

      updated[tplIndex] = {
        ...updated[tplIndex],
        id: meta.id,
        name: meta.name,
        language: meta.language,
        headerFormat: meta.headerFormat,
        mediaType: meta.mediaType,
        sampleMediaUrl: meta.sampleMediaUrl,
        body: meta.body,
        requiresMedia: Boolean(meta.requiresMedia),
        isCarousel,
        carouselCards: newCarouselCards,
        carouselCardCount: meta.carouselCardCount || newCarouselCards.length,
        carouselData: initializedCarouselData
      };
      return updated;
    });
  };

  const handleUploadCardImage = async (seqKey, customIdx, tplIdx, cardIdx, file) => {
    if (!file) return;
    try {
      setUploadingState({ seqKey, customIdx, tplIdx, cardIdx });
      const res = await adminAPI.uploadImage(file);
      const url = res?.data?.data?.url || res?.data?.url || res?.url;
      if (url) {
        updateSequenceTemplates(seqKey, customIdx, (templates) => {
          const updated = [...templates];
          const tpl = { ...updated[tplIdx] };
          const meta = templateMap.get(tpl.id);
          const cards = tpl.carouselCards?.length ? tpl.carouselCards : (meta?.carouselCards || []);
          const currentData = Array.isArray(tpl.carouselData) && tpl.carouselData.length > 0
            ? [...tpl.carouselData]
            : cards.map((c) => ({ mediaUrl: c.media_url || c.mediaUrl || '' }));

          while (currentData.length < cards.length) {
            const idx = currentData.length;
            currentData.push({ mediaUrl: cards[idx]?.media_url || cards[idx]?.mediaUrl || '' });
          }

          currentData[cardIdx] = {
            ...currentData[cardIdx],
            mediaUrl: url
          };

          tpl.carouselData = currentData;
          tpl.isCarousel = true;
          if (!tpl.carouselCards?.length) {
            tpl.carouselCards = cards;
          }
          updated[tplIdx] = tpl;
          return updated;
        });
        showToast({ message: `Card ${cardIdx + 1} photo uploaded successfully!`, severity: 'success' });
      }
    } catch (err) {
      showToast({ message: `Upload failed: ${err?.message || 'Error uploading file'}`, severity: 'error' });
    } finally {
      setUploadingState(null);
    }
  };

  const handleResetCardImage = (seqKey, customIdx, tplIdx, cardIdx) => {
    updateSequenceTemplates(seqKey, customIdx, (templates) => {
      const updated = [...templates];
      const tpl = { ...updated[tplIdx] };
      const meta = templateMap.get(tpl.id);
      const cards = tpl.carouselCards?.length ? tpl.carouselCards : (meta?.carouselCards || []);
      const defaultUrl = cards[cardIdx]?.media_url || cards[cardIdx]?.mediaUrl || '';
      const currentData = Array.isArray(tpl.carouselData) ? [...tpl.carouselData] : [];
      if (currentData[cardIdx]) {
        currentData[cardIdx] = {
          ...currentData[cardIdx],
          mediaUrl: defaultUrl
        };
      }
      tpl.carouselData = currentData;
      updated[tplIdx] = tpl;
      return updated;
    });
    showToast({ message: `Card ${cardIdx + 1} reset to default photo.`, severity: 'info' });
  };

  const handleToggleTemplateEnabled = (seqKey, customIdx, tplIndex) => {
    updateSequenceTemplates(seqKey, customIdx, (templates) => {
      const updated = [...templates];
      updated[tplIndex] = { ...updated[tplIndex], enabled: !updated[tplIndex].enabled };
      return updated;
    });
  };

  const handleMoveTemplateOrder = (seqKey, customIdx, tplIndex, direction) => {
    updateSequenceTemplates(seqKey, customIdx, (templates) => {
      const targetIdx = tplIndex + direction;
      if (targetIdx < 0 || targetIdx >= templates.length) return templates;
      const updated = [...templates];
      const temp = updated[tplIndex];
      updated[tplIndex] = updated[targetIdx];
      updated[targetIdx] = temp;
      return updated;
    });
  };

  const handleRemoveTemplate = (seqKey, customIdx, tplIndex) => {
    updateSequenceTemplates(seqKey, customIdx, (templates) => templates.filter((_, i) => i !== tplIndex));
  };

  const handleUploadMedia = async (seqKey, customIdx, tplIdx, file, isPdf = false) => {
    if (!file) return;
    try {
      setUploadingState({ seqKey, customIdx, tplIdx });
      let res;
      if (isPdf) {
        res = await adminAPI.uploadPdf(file, { name: `${selectedTrek?.name || 'Trek'}-brochure` });
      } else {
        res = await adminAPI.uploadImage(file);
      }
      const url = res?.data?.data?.url || res?.data?.url || res?.url;
      if (url) {
        updateSequenceTemplates(seqKey, customIdx, (templates) => {
          const updated = [...templates];
          updated[tplIdx] = {
            ...updated[tplIdx],
            mediaUrl: url,
            mediaType: isPdf ? 'document' : 'image'
          };
          return updated;
        });
        showToast({ message: `${isPdf ? 'PDF' : 'Photo'} uploaded successfully!`, severity: 'success' });
      }
    } catch (err) {
      showToast({ message: `Upload failed: ${err?.message || 'Error uploading file'}`, severity: 'error' });
    } finally {
      setUploadingState(null);
    }
  };

  const handleRemoveMedia = (seqKey, customIdx, tplIdx) => {
    updateSequenceTemplates(seqKey, customIdx, (templates) => {
      const updated = [...templates];
      updated[tplIdx] = { ...updated[tplIdx], mediaUrl: null };
      return updated;
    });
  };

  const handleAddCustomSequence = () => {
    const nextSeqNum = 3 + (automations.custom_sequences?.length || 0);
    const newSeq = {
      id: `custom_seq_${Date.now()}`,
      title: `Sequence ${nextSeqNum}: Event Milestone`,
      enabled: true,
      daysOffset: 2,
      timing: 'before',
      referenceDate: 'event_start_date',
      bookingStatuses: ['confirmed', 'advanced_paid'],
      packageIds: [],
      stayOptionIds: [],
      templates: []
    };

    setAutomations((prev) => ({
      ...prev,
      custom_sequences: [...(prev.custom_sequences || []), newSeq]
    }));
    setActivePreviewSeq(`custom_${(automations.custom_sequences || []).length}`);
    setIsDirty(true);
    showToast({ message: `Added Sequence ${nextSeqNum}! Configure schedule and templates below.`, severity: 'info' });
  };

  const handleUpdateCustomSequence = (customIdx, field, value) => {
    setAutomations((prev) => {
      const list = [...(prev.custom_sequences || [])];
      list[customIdx] = { ...list[customIdx], [field]: value };
      return { ...prev, custom_sequences: list };
    });
    setIsDirty(true);
  };

  const handleDeleteCustomSequence = (customIdx) => {
    if (!window.confirm('Are you sure you want to remove this scheduled sequence?')) return;
    setAutomations((prev) => {
      const list = prev.custom_sequences.filter((_, i) => i !== customIdx);
      return { ...prev, custom_sequences: list };
    });
    setActivePreviewSeq('sequence1_lead');
    setIsDirty(true);
    showToast({ message: 'Scheduled sequence removed.', severity: 'info' });
  };

  // --- Save Automation ---
  const handleSaveAutomation = async () => {
    if (!selectedTrek) return;
    if (!canManage) {
      showToast({
        message: 'You have view-only access. WhatsApp Automation – Manage permission is required to save changes.',
        severity: 'warning'
      });
      return;
    }
    try {
      setSaving(true);
      const s1Templates = (automations.sequence1_lead?.templates || []).filter((t) => Boolean(t.id));
      const firstActiveS1 = s1Templates.find((t) => t.enabled) || s1Templates[0] || null;

      const payload = {
        sequence1_lead: automations.sequence1_lead,
        sequence2_booking: automations.sequence2_booking,
        custom_sequences: automations.custom_sequences,
        lead_status_automations: automations.lead_status_automations,
        follow_up_done: automations.lead_status_automations?.follow_up_done,
        second_follow_up_done: automations.lead_status_automations?.second_follow_up_done,
        third_follow_up_done: automations.lead_status_automations?.third_follow_up_done,
        templates: s1Templates,
        default: firstActiveS1,
        callback: firstActiveS1,
        pdf: firstActiveS1
      };

      try {
        await trekAPI.updateLeadTemplates(selectedTrek.id, { leadTemplates: payload });
      } catch (saveErr) {
        if (saveErr?.response?.status === 404) {
          await trekAPI.update(selectedTrek.id, { leadTemplates: payload });
        } else {
          throw saveErr;
        }
      }

      queryClient.invalidateQueries(['wa-treks-list']);
      queryClient.invalidateQueries(['treks']);
      setIsDirty(false);
      showToast({ message: `WhatsApp automation saved for "${selectedTrek.name}"!`, severity: 'success' });
    } catch (err) {
      showToast({
        message: err?.response?.data?.message || 'Failed to save WhatsApp automation.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // --- Test Send Handler ---
  const handleTestSend = async () => {
    if (!testPhone) {
      showToast({ message: 'Please enter a test mobile number.', severity: 'warning' });
      return;
    }

    let targetTemplates = [];
    if (testSequenceKey === 'sequence1_lead') {
      targetTemplates = automations.sequence1_lead?.templates || [];
    } else if (testSequenceKey === 'sequence2_booking') {
      targetTemplates = automations.sequence2_booking?.templates || [];
    } else if (testSequenceKey?.startsWith('custom_')) {
      const idx = parseInt(testSequenceKey.split('_')[1], 10);
      targetTemplates = automations.custom_sequences?.[idx]?.templates || [];
    } else if (testSequenceKey?.startsWith('status_')) {
      const statusKey = testSequenceKey.replace('status_', '');
      targetTemplates = automations.lead_status_automations?.[statusKey]?.templates || [];
    }

    if (!canManage) {
      showToast({
        message: 'You have view-only access. WhatsApp Automation – Manage permission is required to send test messages.',
        severity: 'warning'
      });
      return;
    }

    const activeList = targetTemplates.filter((t) => t.enabled && t.id);
    if (activeList.length === 0) {
      showToast({ message: 'No enabled templates in the selected sequence to send.', severity: 'warning' });
      return;
    }

    try {
      setTestSending(true);
      for (const tpl of activeList) {
        const meta = templateMap.get(tpl.id);
        const isCarousel = Boolean(tpl.isCarousel || meta?.isCarousel);
        const cards = tpl.carouselCards?.length ? tpl.carouselCards : (meta?.carouselCards || []);
        await crmAPI.sendLeadWhatsAppTemplate({
          phone: testPhone,
          recipientName: testName,
          trekId: selectedTrek?.id,
          templateId: tpl.id,
          templateName: tpl.name,
          language: tpl.language || 'en_US',
          mediaUrl: tpl.mediaUrl || null,
          templateHeaderFormat: isCarousel ? 'CAROUSEL' : tpl.headerFormat,
          isCarousel,
          carouselCards: cards,
          carouselData: tpl.carouselData || []
        });
      }
      showToast({ message: `Test WhatsApp message(s) sent to ${testPhone}!`, severity: 'success' });
      setTestSendOpen(false);
    } catch (err) {
      showToast({
        message: err?.response?.data?.message || 'Failed to send test WhatsApp message.',
        severity: 'error'
      });
    } finally {
      setTestSending(false);
    }
  };

  // --- Bulk Copy Handler ---
  const handleBulkCopy = async () => {
    if (targetTrekIds.length === 0) return;
    if (!canManage) {
      showToast({
        message: 'You have view-only access. WhatsApp Automation – Manage permission is required to copy settings.',
        severity: 'warning'
      });
      return;
    }
    try {
      setCopying(true);
      const s1Templates = (automations.sequence1_lead?.templates || []).filter((t) => Boolean(t.id));
      const firstActiveS1 = s1Templates.find((t) => t.enabled) || s1Templates[0] || null;

      const payload = {
        sequence1_lead: automations.sequence1_lead,
        sequence2_booking: automations.sequence2_booking,
        custom_sequences: automations.custom_sequences,
        lead_status_automations: automations.lead_status_automations,
        follow_up_done: automations.lead_status_automations?.follow_up_done,
        second_follow_up_done: automations.lead_status_automations?.second_follow_up_done,
        third_follow_up_done: automations.lead_status_automations?.third_follow_up_done,
        templates: s1Templates,
        default: firstActiveS1,
        callback: firstActiveS1,
        pdf: firstActiveS1
      };

      for (const targetId of targetTrekIds) {
        try {
          await trekAPI.updateLeadTemplates(targetId, { leadTemplates: payload });
        } catch (saveErr) {
          if (saveErr?.response?.status === 404) {
            await trekAPI.update(targetId, { leadTemplates: payload });
          } else {
            throw saveErr;
          }
        }
      }
      queryClient.invalidateQueries(['wa-treks-list']);
      showToast({
        message: `Applied full automation lifecycle to ${targetTrekIds.length} trek(s)!`,
        severity: 'success'
      });
      setCopyModalOpen(false);
      setTargetTrekIds([]);
    } catch {
      showToast({ message: 'Failed to copy settings to target treks.', severity: 'error' });
    } finally {
      setCopying(false);
    }
  };

  // Filtered Treks List
  const filteredTreks = useMemo(() => {
    if (statusFilter === 'all') return treks;
    return treks.filter((t) => {
      const cnt = countTotalConfigured(t);
      if (statusFilter === 'configured') return cnt > 0;
      if (statusFilter === 'unconfigured') return cnt === 0;
      return true;
    });
  }, [treks, statusFilter]);

  const configuredCount = useMemo(() => {
    return treks.filter((t) => countTotalConfigured(t) > 0).length;
  }, [treks]);

  // Active templates for live phone mockup preview
  const previewTemplates = useMemo(() => {
    if (activePreviewSeq === 'sequence1_lead') {
      return (automations.sequence1_lead?.templates || []).filter((t) => t.enabled);
    }
    if (activePreviewSeq === 'sequence2_booking') {
      return (automations.sequence2_booking?.templates || []).filter((t) => t.enabled);
    }
    if (activePreviewSeq?.startsWith('custom_')) {
      const idx = parseInt(activePreviewSeq.split('_')[1], 10);
      return (automations.custom_sequences?.[idx]?.templates || []).filter((t) => t.enabled);
    }
    if (activePreviewSeq?.startsWith('status_')) {
      const statusKey = activePreviewSeq.replace('status_', '');
      return (automations.lead_status_automations?.[statusKey]?.templates || []).filter((t) => t.enabled);
    }
    return [];
  }, [automations, activePreviewSeq]);

  // Render Accordion-based Template Item Card
  const renderTemplateAccordion = (tpl, tplIdx, seqKey, customIdx = null) => {
    const meta = templateMap.get(tpl.id);
    const isCarousel = Boolean(
      tpl.isCarousel ||
      meta?.isCarousel ||
      String(tpl.headerFormat || meta?.headerFormat || '').toUpperCase() === 'CAROUSEL'
    );
    const headerFormat = isCarousel ? 'CAROUSEL' : (tpl.headerFormat || meta?.headerFormat || '').toUpperCase();
    const isImageHeader = headerFormat === 'IMAGE';
    const isDocHeader = headerFormat === 'DOCUMENT';
    const effectiveCards = (Array.isArray(tpl.carouselCards) && tpl.carouselCards.length > 0)
      ? tpl.carouselCards
      : (Array.isArray(meta?.carouselCards) ? meta.carouselCards : []);
    const accordionId = customIdx !== null ? `custom_${customIdx}_${tplIdx}` : `${seqKey}_${tplIdx}`;
    const isOpen = Boolean(expandedAccordions[accordionId]);
    const isUploadingThis =
      uploadingState?.seqKey === seqKey &&
      uploadingState?.customIdx === customIdx &&
      uploadingState?.tplIdx === tplIdx;

    return (
      <div
        key={`${tpl.id}-${tplIdx}`}
        className={`wa-template-accordion ${isOpen ? 'open' : ''} ${!tpl.enabled ? 'disabled' : ''}`}
        onClick={() => {
          setActivePreviewSeq(customIdx !== null ? `custom_${customIdx}` : seqKey);
        }}
      >
        {/* Accordion Summary Header */}
        <div
          className="wa-template-accordion-header"
          onClick={(e) => {
            e.stopPropagation();
            toggleAccordion(accordionId);
            setActivePreviewSeq(customIdx !== null ? `custom_${customIdx}` : seqKey);
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <span className="wa-template-step-badge">Template {tplIdx + 1}</span>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {tpl.name || 'Select Template'}
            </Typography>
            {headerFormat && (
              <Chip
                size="small"
                label={isCarousel ? `CAROUSEL (${effectiveCards.length || tpl.carouselCardCount || meta?.carouselCardCount || 0})` : headerFormat}
                color={isCarousel ? 'secondary' : isImageHeader ? 'success' : isDocHeader ? 'info' : 'default'}
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  ...(isCarousel ? { bgcolor: '#8b5cf6', color: '#ffffff' } : {})
                }}
              />
            )}
            {tpl.mediaUrl && (
              <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {isImageHeader ? '📷 Photo' : '📄 PDF'}
              </Typography>
            )}
            {isCarousel && effectiveCards.length > 0 && (
              <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                🎠 {effectiveCards.length} Cards
              </Typography>
            )}
          </div>

          <div className="wa-template-accordion-actions" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Move Up">
              <span>
                <IconButton
                  size="small"
                  disabled={tplIdx === 0}
                  onClick={() => handleMoveTemplateOrder(seqKey, customIdx, tplIdx, -1)}
                >
                  <ArrowUpwardRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move Down">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleMoveTemplateOrder(seqKey, customIdx, tplIdx, 1)}
                >
                  <ArrowDownwardRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={tpl.enabled}
                  onChange={() => handleToggleTemplateEnabled(seqKey, customIdx, tplIdx)}
                  color="success"
                />
              }
              label={
                <Typography variant="caption" sx={{ fontWeight: 600, color: tpl.enabled ? '#166534' : '#94a3b8' }}>
                  {tpl.enabled ? 'Active' : 'Disabled'}
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <Tooltip title="Remove Template from Sequence">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemoveTemplate(seqKey, customIdx, tplIdx)}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => toggleAccordion(accordionId)}>
              {isOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            </IconButton>
          </div>
        </div>

        {/* Accordion Body (Configurable when expanded) */}
        {isOpen && (
          <div className="wa-template-accordion-body">
            <div>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', mb: 0.5, display: 'block' }}>
                Waplify Template Selection
              </Typography>
              <Select
                size="small"
                fullWidth
                value={tpl.id}
                onChange={(e) => handleChangeTemplateSelection(seqKey, customIdx, tplIdx, e.target.value)}
                sx={{ bgcolor: '#ffffff' }}
              >
                {waplifyTemplates.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {w.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={w.isCarousel ? `CAROUSEL (${w.carouselCardCount || w.carouselCards?.length || ''})` : (w.headerFormat || 'TEXT')}
                          sx={{
                            height: 18,
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            ...(w.isCarousel ? { bgcolor: '#ede9fe', color: '#6d28d9' } : {})
                          }}
                        />
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          ({w.language})
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </div>

            {/* Header Media Attachment (Photo for IMAGE, PDF for DOCUMENT) */}
            {(isImageHeader || isDocHeader) && (
              <div className="wa-media-box">
                <div className="wa-media-header">
                  <div className="wa-media-header-label">
                    {isImageHeader ? <ImageRoundedIcon color="success" fontSize="small" /> : <PictureAsPdfRoundedIcon color="error" fontSize="small" />}
                    <span>{isImageHeader ? 'Header Photo Attachment' : 'Header PDF Attachment'}</span>
                  </div>
                  {isDocHeader && !tpl.mediaUrl && selectedTrek?.brochureUrl && (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                      onClick={() => {
                        updateSequenceTemplates(seqKey, customIdx, (templates) => {
                          const updated = [...templates];
                          updated[tplIdx] = {
                            ...updated[tplIdx],
                            mediaUrl: selectedTrek.brochureUrl,
                            mediaType: 'document'
                          };
                          return updated;
                        });
                      }}
                    >
                      Use Trek Brochure PDF
                    </Button>
                  )}
                </div>

                {tpl.mediaUrl ? (
                  <div className="wa-media-preview-row">
                    {isImageHeader ? (
                      <img src={tpl.mediaUrl} alt="Header" className="wa-media-thumb" />
                    ) : (
                      <PictureAsPdfRoundedIcon sx={{ fontSize: 44, color: '#ef4444' }} />
                    )}
                    <div className="wa-media-preview-info">
                      <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>
                        {isImageHeader ? 'Custom Header Photo' : 'Custom PDF Document'}
                      </div>
                      <a href={tpl.mediaUrl} target="_blank" rel="noopener noreferrer">
                        {tpl.mediaUrl.length > 40 ? `${tpl.mediaUrl.substring(0, 40)}...` : tpl.mediaUrl} <OpenInNewRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                      </a>
                    </div>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => handleRemoveMedia(seqKey, customIdx, tplIdx)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Replace / Remove
                    </Button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <input
                      type="file"
                      id={`media-upload-${seqKey}-${customIdx ?? 'root'}-${tplIdx}`}
                      style={{ display: 'none' }}
                      accept={isImageHeader ? 'image/*' : 'application/pdf'}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadMedia(seqKey, customIdx, tplIdx, file, isDocHeader);
                      }}
                    />
                    <label htmlFor={`media-upload-${seqKey}-${customIdx ?? 'root'}-${tplIdx}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        size="small"
                        startIcon={isUploadingThis ? <CircularProgress size={16} /> : <CloudUploadRoundedIcon />}
                        disabled={isUploadingThis}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {isUploadingThis ? 'Uploading...' : `Upload ${isImageHeader ? 'Photo' : 'PDF'}`}
                      </Button>
                    </label>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {isImageHeader ? 'Supported: JPG, PNG, WEBP (Max 5MB)' : 'Supported: PDF (Max 15MB)'}
                    </Typography>
                  </div>
                )}
              </div>
            )}

            {/* Carousel Multi-Photo Cards Manager */}
            {isCarousel && (
              <div className="wa-carousel-section">
                <div className="wa-carousel-section-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e1b4b', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      🎡 Carousel Cards ({effectiveCards.length} Cards)
                    </Typography>
                    <Chip
                      label="Multi-Photo Cards"
                      size="small"
                      sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 600 }}
                    />
                  </div>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Upload or replace high-resolution photos for each card below. These images will be sent directly in the WhatsApp carousel.
                  </Typography>
                </div>

                <div className="wa-carousel-cards-grid">
                  {effectiveCards.map((card, cIdx) => {
                    const configuredUrl = tpl.carouselData?.[cIdx]?.mediaUrl || '';
                    const defaultCardUrl = card.media_url || card.mediaUrl || '';
                    const activeCardImg = configuredUrl || defaultCardUrl;
                    const isCustomImg = Boolean(configuredUrl && configuredUrl !== defaultCardUrl);
                    const isUploadingCard = isUploadingThis && uploadingState?.cardIdx === cIdx;

                    return (
                      <div key={cIdx} className="wa-carousel-card-item">
                        <div className="wa-carousel-card-item-header">
                          <span className="wa-carousel-card-badge">Card {cIdx + 1}</span>
                          {isCustomImg ? (
                            <Chip label="Custom Photo" size="small" sx={{ height: 16, fontSize: '0.58rem', bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600 }} />
                          ) : (
                            <Chip label="Default Photo" size="small" sx={{ height: 16, fontSize: '0.58rem', bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 500 }} />
                          )}
                        </div>

                        {/* Card Image Preview */}
                        <div className="wa-carousel-card-img-wrap">
                          {activeCardImg ? (
                            <img src={activeCardImg} alt={`Card ${cIdx + 1}`} className="wa-carousel-card-img" />
                          ) : (
                            <div className="wa-carousel-card-img-placeholder">
                              <ImageRoundedIcon sx={{ fontSize: 32, color: '#94a3b8' }} />
                              <span>No Photo</span>
                            </div>
                          )}
                          {isUploadingCard && (
                            <div className="wa-carousel-card-loading">
                              <CircularProgress size={24} sx={{ color: '#ffffff' }} />
                            </div>
                          )}
                        </div>

                        {/* Card Text & Buttons */}
                        <div className="wa-carousel-card-info">
                          <div className="wa-carousel-card-body-text" title={card.body_text || card.body}>
                            {card.body_text || card.body || `Card ${cIdx + 1}`}
                          </div>
                          {Array.isArray(card.buttons) && card.buttons.length > 0 && (
                            <div className="wa-carousel-card-buttons-preview">
                              {card.buttons.map((btn, bIdx) => (
                                <span key={bIdx} className="wa-carousel-card-btn-tag" title={btn.url}>
                                  🔗 {btn.text || btn.url || 'Action'}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Action buttons */}
                        <div className="wa-carousel-card-actions">
                          <input
                            type="file"
                            id={`card-upload-${seqKey}-${customIdx ?? 'root'}-${tplIdx}-${cIdx}`}
                            style={{ display: 'none' }}
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleUploadCardImage(seqKey, customIdx, tplIdx, cIdx, f);
                            }}
                          />
                          <label htmlFor={`card-upload-${seqKey}-${customIdx ?? 'root'}-${tplIdx}-${cIdx}`} style={{ flex: 1 }}>
                            <Button
                              component="span"
                              size="small"
                              variant="outlined"
                              fullWidth
                              disabled={isUploadingCard}
                              startIcon={<CloudUploadRoundedIcon sx={{ fontSize: 14 }} />}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                borderColor: '#cbd5e1',
                                color: '#334155'
                              }}
                            >
                              {isUploadingCard ? 'Uploading...' : isCustomImg ? 'Change Photo' : 'Upload Photo'}
                            </Button>
                          </label>
                          {isCustomImg && (
                            <Tooltip title="Reset to template default photo">
                              <IconButton
                                size="small"
                                onClick={() => handleResetCardImage(seqKey, customIdx, tplIdx, cIdx)}
                              >
                                <RefreshRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderLeadStatusCard = (statusKey, title, badgeLabel, colorTheme, icon, triggerDesc) => {
    const statusData = automations.lead_status_automations?.[statusKey] || {
      enabled: true,
      title,
      templates: []
    };
    const seqKey = `status_${statusKey}`;

    return (
      <div key={statusKey} className={`wa-sequence-card ${!statusData.enabled ? 'disabled' : ''}`}>
        <div className={`wa-sequence-header ${colorTheme}`}>
          <div className="wa-sequence-title-group">
            <span className={`wa-step-badge ${colorTheme}`}>{badgeLabel}</span>
            <div>
              <div className="wa-sequence-title-text">{title}</div>
              <div className="wa-sequence-desc-text">CRM Lead Status Automation • Triggered on Status Change</div>
            </div>
          </div>
          <div className="wa-sequence-actions">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={statusData.enabled}
                  onChange={() => handleToggleSequenceEnabled(seqKey)}
                  color="warning"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: statusData.enabled ? '#0f172a' : '#94a3b8' }}>
                  {statusData.enabled ? 'Automation Active' : 'Automation Paused'}
                </Typography>
              }
            />
          </div>
        </div>

        <div className={`wa-trigger-callout ${colorTheme}`}>
          <div className="wa-trigger-callout-icon">{icon}</div>
          <div className="wa-trigger-callout-body">
            <span className="wa-trigger-callout-title">{title}: Automated CRM Trigger</span>
            <p>
              When a participant's CRM lead status is changed to <strong>{title}</strong> for <strong>{selectedTrek?.name}</strong>, all enabled templates below will be dispatched sequentially via WhatsApp.
            </p>
          </div>
        </div>

        <div className="wa-sequence-templates">
          {statusData.templates?.length === 0 ? (
            <div className="wa-sequence-empty">
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155', mb: 0.5 }}>
                No template configured for {title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2, maxWidth: 440, mx: 'auto' }}>
                Select a Waplify template to send when lead status is marked as {title}.
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={() => handleAddTemplateToSequence(seqKey)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Add Template for {title}
              </Button>
            </div>
          ) : (
            <>
              {statusData.templates?.map((tpl, idx) =>
                renderTemplateAccordion(tpl, idx, seqKey, null)
              )}
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={() => handleAddTemplateToSequence(seqKey)}
                sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', mt: 0.5 }}
              >
                Add Another Template for {title}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="wa-container">
      {/* Page Header */}
      <PageHeader
        title="WhatsApp Automation Studio"
        subtitle="Centrally orchestrate the complete participant messaging lifecycle across inquiry, booking confirmation, and scheduled event journey milestones."
        action={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => {
                refetchTemplates();
                queryClient.invalidateQueries(['wa-treks-list']);
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Refresh Templates
            </Button>
          </div>
        }
      />

      {!canManage && (
        <Typography
          variant="body2"
          sx={{
            mb: 2.5,
            p: 1.5,
            bgcolor: '#fff7ed',
            border: '1px solid #fed7aa',
            borderRadius: 2,
            color: '#9a3412',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <span>🔒</span>
          <span>
            You have <strong>view-only</strong> access. Ask a super admin to grant <strong>WhatsApp Automation – Manage</strong> on your role (Role Builder) or your user account to edit sequences, upload attachments, or send messages.
          </span>
        </Typography>
      )}

      {/* Top Metric Cards */}
      <div className="wa-stats-grid">
        <div className="wa-stat-card">
          <div className="wa-stat-icon-wrap green">
            <WhatsAppIcon />
          </div>
          <div className="wa-stat-info">
            <h4>{configuredCount}</h4>
            <p>Treks Configured</p>
          </div>
        </div>

        <div className="wa-stat-card">
          <div className="wa-stat-icon-wrap blue">
            <FlashOnRoundedIcon />
          </div>
          <div className="wa-stat-info">
            <h4>
              {automations.sequence1_lead?.templates?.filter((t) => t.enabled)?.length +
                automations.sequence2_booking?.templates?.filter((t) => t.enabled)?.length +
                (automations.custom_sequences?.reduce((sum, cs) => sum + (cs.templates?.filter((t) => t.enabled)?.length || 0), 0) || 0) +
                ['follow_up_done', 'second_follow_up_done', 'third_follow_up_done'].reduce(
                  (sum, k) => sum + (automations.lead_status_automations?.[k]?.templates?.filter((t) => t.enabled)?.length || 0),
                  0
                )}
            </h4>
            <p>Active Templates (Current Trek)</p>
          </div>
        </div>

        <div className="wa-stat-card">
          <div className="wa-stat-icon-wrap amber">
            <HikingRoundedIcon />
          </div>
          <div className="wa-stat-info">
            <h4>{treks.length - configuredCount}</h4>
            <p>Pending Treks</p>
          </div>
        </div>

        <div className="wa-stat-card">
          <div className="wa-stat-icon-wrap purple">
            <ScheduleRoundedIcon />
          </div>
          <div className="wa-stat-info">
            <h4>{waplifyTemplates.length}</h4>
            <p>Waplify Templates</p>
          </div>
        </div>
      </div>

      {/* Select Trek Card */}
      <div className="wa-card">
        <div className="wa-section-header">
          <div className="wa-section-title">
            <span className="wa-step-badge black">Select Trek</span>
            <span>Select a Trek to Configure</span>
          </div>

          <div className="wa-filter-tabs">
            <button
              className={`wa-filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All Treks ({treks.length})
            </button>
            <button
              className={`wa-filter-tab ${statusFilter === 'configured' ? 'active' : ''}`}
              onClick={() => setStatusFilter('configured')}
            >
              Configured ({configuredCount})
            </button>
            <button
              className={`wa-filter-tab ${statusFilter === 'unconfigured' ? 'active' : ''}`}
              onClick={() => setStatusFilter('unconfigured')}
            >
              Pending ({treks.length - configuredCount})
            </button>
          </div>
        </div>

        <Autocomplete
          fullWidth
          options={filteredTreks}
          getOptionLabel={(option) => `${option.name} (${option.code || option.id})`}
          value={selectedTrek}
          onChange={(_, newValue) => handleSelectTrek(newValue)}
          loading={treksLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search by trek name or code (e.g. Kedarkantha, Dev Hatra, DH)..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <Box sx={{ color: '#64748b', display: 'flex', alignItems: 'center', pl: 1, pr: 0.5 }}>
                    <HikingRoundedIcon fontSize="small" />
                  </Box>
                )
              }}
            />
          )}
          renderOption={(props, option) => {
            const cnt = countTotalConfigured(option);
            const isConfigured = cnt > 0;
            return (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 0.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Code: {option.code || 'N/A'} • {getCategoryLabel(option.category)}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={isConfigured ? `${cnt} Template(s)` : 'No Automation'}
                    color={isConfigured ? 'success' : 'default'}
                    sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600 }}
                  />
                </Box>
              </li>
            );
          }}
        />

        {selectedTrek && (
          <div className="wa-selected-trek-info">
            <div>
              <h4>{selectedTrek.name}</h4>
              <p>
                <span>Code: <strong>{selectedTrek.code || 'N/A'}</strong></span>
                <span>•</span>
                <span>Slug: <code>{selectedTrek.slug}</code></span>
                <span>•</span>
                <span>Category: <strong>{getCategoryLabel(selectedTrek.category)}</strong></span>
                <span>•</span>
                <span>Brochure: {selectedTrek.brochureUrl ? <a href={selectedTrek.brochureUrl} target="_blank" rel="noopener noreferrer">View PDF ↗</a> : 'None'}</span>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <Button
                size="small"
                variant="outlined"
                component={Link}
                to={`/treks`}
                endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Open Trek Details
              </Button>
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={() => handleSelectTrek(null)}
                sx={{ textTransform: 'none' }}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Trek Automation Studio (2-Column Layout with Sticky WhatsApp Phone Mockup) */}
      {selectedTrek ? (
        <div className="wa-studio-layout">
          {/* LEFT COLUMN: Sequence & Template Configuration */}
          <div className="wa-studio-main">
            {/* Top Quick Actions Bar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.85rem' }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                WhatsApp Lifecycle Automation for {selectedTrek.name}
              </Typography>
              <div style={{ display: 'flex', gap: '0.65rem' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SendRoundedIcon />}
                  disabled={!canManage}
                  onClick={() => {
                    setTestSequenceKey(activePreviewSeq);
                    setTestSendOpen(true);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Test Send
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyRoundedIcon />}
                  disabled={!canManage}
                  onClick={() => {
                    setTargetTrekIds([]);
                    setCopyModalOpen(true);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Copy to Other Treks
                </Button>
              </div>
            </div>

            {/* ================= SEQUENCE 1: LEAD INGESTION ================= */}
            <div className={`wa-sequence-card ${!automations.sequence1_lead?.enabled ? 'disabled' : ''}`}>
              <div className="wa-sequence-header green">
                <div className="wa-sequence-title-group">
                  <span className="wa-step-badge green">Sequence 1</span>
                  <div>
                    <div className="wa-sequence-title-text">Lead Creation Automation</div>
                    <div className="wa-sequence-desc-text">Inquiry Stage • Triggered when lead is generated</div>
                  </div>
                </div>
                <div className="wa-sequence-actions">
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={automations.sequence1_lead?.enabled}
                        onChange={() => handleToggleSequenceEnabled('sequence1_lead')}
                        color="success"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600, color: automations.sequence1_lead?.enabled ? '#166534' : '#94a3b8' }}>
                        {automations.sequence1_lead?.enabled ? 'Sequence Active' : 'Sequence Paused'}
                      </Typography>
                    }
                  />
                </div>
              </div>

              {/* Clean Explainer callout banner without line breaks */}
              <div className="wa-trigger-callout green">
                <div className="wa-trigger-callout-icon">⚡</div>
                <div className="wa-trigger-callout-body">
                  <span className="wa-trigger-callout-title">Sequence 1: Lead Ingestion Automation</span>
                  <p>
                    When a participant creates a lead for <strong>{selectedTrek.name}</strong>, all enabled templates in this sequence will be sent sequentially via WhatsApp. You can chain multiple templates (e.g. 1 Image greeting + 1 PDF itinerary). By default, triggers apply across <strong>Get PDF</strong>, <strong>Callback Request</strong>, and <strong>Manual CRM Leads</strong>.
                  </p>
                </div>
              </div>

              <div className="wa-sequence-templates">
                {automations.sequence1_lead?.templates?.length === 0 ? (
                  <div className="wa-sequence-empty">
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155', mb: 0.5 }}>
                      No templates configured for Sequence 1
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2, maxWidth: 440, mx: 'auto' }}>
                      Add one or more Waplify templates to immediately greet participants when their lead is created.
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<AddCircleOutlineRoundedIcon />}
                      onClick={() => handleAddTemplateToSequence('sequence1_lead')}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Add Template to Sequence 1
                    </Button>
                  </div>
                ) : (
                  <>
                    {automations.sequence1_lead?.templates?.map((tpl, idx) =>
                      renderTemplateAccordion(tpl, idx, 'sequence1_lead', null)
                    )}
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<AddCircleOutlineRoundedIcon />}
                      onClick={() => handleAddTemplateToSequence('sequence1_lead')}
                      sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', mt: 0.5 }}
                    >
                      Add Another Template to Sequence 1
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* ================= SEQUENCE 2: BOOKING CONFIRMED ================= */}
            <div className={`wa-sequence-card ${!automations.sequence2_booking?.enabled ? 'disabled' : ''}`}>
              <div className="wa-sequence-header blue">
                <div className="wa-sequence-title-group">
                  <span className="wa-step-badge blue">Sequence 2</span>
                  <div>
                    <div className="wa-sequence-title-text">Booking Confirmed / Advance Paid</div>
                    <div className="wa-sequence-desc-text">Conversion Stage • Triggered when payment is made</div>
                  </div>
                </div>
                <div className="wa-sequence-actions">
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={automations.sequence2_booking?.enabled}
                        onChange={() => handleToggleSequenceEnabled('sequence2_booking')}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600, color: automations.sequence2_booking?.enabled ? '#0369a1' : '#94a3b8' }}>
                        {automations.sequence2_booking?.enabled ? 'Sequence Active' : 'Sequence Paused'}
                      </Typography>
                    }
                  />
                </div>
              </div>

              <div className="wa-trigger-callout blue">
                <div className="wa-trigger-callout-icon">💳</div>
                <div className="wa-trigger-callout-body">
                  <span className="wa-trigger-callout-title">Sequence 2: Booking & Payment Confirmation</span>
                  <p>
                    When a participant's booking status changes to <strong>Confirmed</strong> or <strong>Advance Paid</strong> for <strong>{selectedTrek.name}</strong>, all enabled templates in this sequence will be sent sequentially via WhatsApp (e.g. Booking confirmation receipt + WhatsApp group link).
                  </p>
                </div>
              </div>

              <div className="wa-sequence-templates">
                {automations.sequence2_booking?.templates?.length === 0 ? (
                  <div className="wa-sequence-empty">
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155', mb: 0.5 }}>
                      No templates configured for Sequence 2
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2, maxWidth: 440, mx: 'auto' }}>
                      Configure automated confirmation templates sent as soon as payment is verified.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<AddCircleOutlineRoundedIcon />}
                      onClick={() => handleAddTemplateToSequence('sequence2_booking')}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Add Template to Sequence 2
                    </Button>
                  </div>
                ) : (
                  <>
                    {automations.sequence2_booking?.templates?.map((tpl, idx) =>
                      renderTemplateAccordion(tpl, idx, 'sequence2_booking', null)
                    )}
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<AddCircleOutlineRoundedIcon />}
                      onClick={() => handleAddTemplateToSequence('sequence2_booking')}
                      sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', mt: 0.5 }}
                    >
                      Add Another Template to Sequence 2
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* ================= DYNAMIC SCHEDULED SEQUENCES (SEQUENCE 3+) ================= */}
            {automations.custom_sequences?.map((seq, customIdx) => {
              const seqNum = customIdx + 3;

              const currentStatuses = seq.bookingStatuses || ['confirmed', 'advanced_paid'];
              const isAllStatusesSelected =
                currentStatuses.length === FORMATTED_REGISTRATION_STATUS_OPTIONS.length;

              const currentPackageIds = seq.packageIds || [];
              const isAllPackagesSelected =
                currentPackageIds.length === 0 ||
                (availablePackages.length > 0 && currentPackageIds.length === availablePackages.length);

              const currentStayIds = seq.stayOptionIds || [];
              const isAllStaysSelected =
                currentStayIds.length === 0 ||
                (availableStayOptions.length > 0 && currentStayIds.length === availableStayOptions.length);

              const isCustomFiltered =
                !isAllStatusesSelected ||
                (availablePackages.length > 0 && !isAllPackagesSelected) ||
                (availableStayOptions.length > 0 && !isAllStaysSelected);

              // Summaries for callout banner
              const statusSummary =
                currentStatuses.length === 0
                  ? 'None Selected'
                  : isAllStatusesSelected
                  ? 'All Booking Statuses'
                  : currentStatuses
                      .map((s) => FORMATTED_REGISTRATION_STATUS_OPTIONS.find((o) => o.value === s)?.label || s)
                      .join(', ');

              const packageSummary =
                availablePackages.length === 0
                  ? 'All Packages'
                  : isAllPackagesSelected
                  ? 'All Packages'
                  : currentPackageIds
                      .map((id) => availablePackages.find((p) => String(p.id) === String(id))?.name || `Package #${id}`)
                      .join(', ');

              const staySummary =
                availableStayOptions.length === 0
                  ? null
                  : isAllStaysSelected
                  ? 'All Stays'
                  : currentStayIds
                      .map((id) => availableStayOptions.find((o) => String(o.id || o.name) === String(id))?.name || id)
                      .join(', ');

              const handleBookingStatusChange = (e) => {
                const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                if (val.includes('__all__')) {
                  const isCurrentlyAll = currentStatuses.length === FORMATTED_REGISTRATION_STATUS_OPTIONS.length;
                  handleUpdateCustomSequence(
                    customIdx,
                    'bookingStatuses',
                    isCurrentlyAll ? [] : FORMATTED_REGISTRATION_STATUS_OPTIONS.map((o) => o.value)
                  );
                } else {
                  handleUpdateCustomSequence(customIdx, 'bookingStatuses', val);
                }
              };

              const handlePackageChange = (e) => {
                const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                if (val.includes('__all__')) {
                  handleUpdateCustomSequence(
                    customIdx,
                    'packageIds',
                    isAllPackagesSelected ? [] : availablePackages.map((p) => String(p.id))
                  );
                } else {
                  handleUpdateCustomSequence(customIdx, 'packageIds', val);
                }
              };

              const handleStayChange = (e) => {
                const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                if (val.includes('__all__')) {
                  handleUpdateCustomSequence(
                    customIdx,
                    'stayOptionIds',
                    isAllStaysSelected ? [] : availableStayOptions.map((s) => String(s.id || s.name))
                  );
                } else {
                  handleUpdateCustomSequence(customIdx, 'stayOptionIds', val);
                }
              };

              return (
                <div key={seq.id || customIdx} className={`wa-sequence-card ${!seq.enabled ? 'disabled' : ''}`}>
                  <div className="wa-sequence-header purple">
                    <div className="wa-sequence-title-group">
                      <span className="wa-step-badge purple">Sequence {seqNum}</span>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span className="wa-sequence-title-text">{seq.title || `Scheduled Sequence ${seqNum}`}</span>
                          {isCustomFiltered && (
                            <Chip
                              label="Filtered Audience"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.62rem',
                                bgcolor: '#ede9fe',
                                color: '#6d28d9',
                                fontWeight: 700,
                                border: '1px solid #ddd6fe'
                              }}
                            />
                          )}
                        </div>
                        <div className="wa-sequence-desc-text">
                          Scheduled Journey Trigger • {seq.daysOffset} day(s) {seq.timing} {seq.referenceDate === 'event_start_date' ? 'Event Start Date' : 'Event End Date'}
                        </div>
                      </div>
                    </div>
                    <div className="wa-sequence-actions">
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={seq.enabled}
                            onChange={() => handleToggleSequenceEnabled(null, customIdx)}
                            color="secondary"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 600, color: seq.enabled ? '#7e22ce' : '#94a3b8' }}>
                            {seq.enabled ? 'Active' : 'Paused'}
                          </Typography>
                        }
                      />
                      <Tooltip title="Delete this scheduled sequence">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteCustomSequence(customIdx)}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  {/* Trigger Configuration Bar */}
                  <div className="wa-trigger-config-bar">
                    <span style={{ fontWeight: 700, color: '#0f172a' }}>Sequence Name:</span>
                    <TextField
                      size="small"
                      value={seq.title}
                      onChange={(e) => handleUpdateCustomSequence(customIdx, 'title', e.target.value)}
                      placeholder="e.g. Packing Checklist, Feedback Form"
                      sx={{ width: 260, bgcolor: '#ffffff' }}
                    />

                    <span style={{ fontWeight: 700, color: '#0f172a', ml: 1 }}>Send Trigger:</span>
                    <div className="wa-trigger-config-pill">
                      <TextField
                        type="number"
                        size="small"
                        value={seq.daysOffset}
                        onChange={(e) =>
                          handleUpdateCustomSequence(customIdx, 'daysOffset', Math.max(0, parseInt(e.target.value) || 0))
                        }
                        sx={{ width: 70 }}
                        inputProps={{ min: 0 }}
                      />
                      <span>day(s)</span>
                      <Select
                        size="small"
                        value={seq.timing}
                        onChange={(e) => handleUpdateCustomSequence(customIdx, 'timing', e.target.value)}
                        sx={{ minWidth: 95 }}
                      >
                        <MenuItem value="before">Before</MenuItem>
                        <MenuItem value="after">After</MenuItem>
                      </Select>
                      <span>the</span>
                      <Select
                        size="small"
                        value={seq.referenceDate}
                        onChange={(e) => handleUpdateCustomSequence(customIdx, 'referenceDate', e.target.value)}
                        sx={{ minWidth: 160 }}
                      >
                        <MenuItem value="event_start_date">Event Start Date</MenuItem>
                        <MenuItem value="event_end_date">Event End Date</MenuItem>
                      </Select>
                    </div>
                  </div>

                  {/* Sequence 3+ Audience & Segment Filters (Multi-Select) */}
                  <div className="wa-sequence-filters-bar">
                    <div className="wa-filters-bar-title">
                      <FilterListRoundedIcon sx={{ fontSize: 17, color: '#7c3aed' }} />
                      <span>Audience Filters:</span>
                    </div>

                    {/* 1. Participant Booking Status Multi-Select */}
                    <div className="wa-filter-item">
                      <span className="wa-filter-label">Booking Status:</span>
                      <FormControl size="small" sx={{ minWidth: 190, maxWidth: 270 }}>
                        <Select
                          multiple
                          size="small"
                          value={currentStatuses}
                          onChange={handleBookingStatusChange}
                          MenuProps={filterSelectMenuProps}
                          sx={{ bgcolor: '#ffffff', fontSize: '0.82rem', height: 36, borderRadius: '8px' }}
                          renderValue={(selected) => {
                            if (!selected || selected.length === 0) return <span style={{ color: '#94a3b8' }}>None Selected</span>;
                            if (selected.length === FORMATTED_REGISTRATION_STATUS_OPTIONS.length) return 'All Booking Statuses';
                            if (selected.length <= 2) {
                              return selected
                                .map((s) => FORMATTED_REGISTRATION_STATUS_OPTIONS.find((o) => o.value === s)?.label || s)
                                .join(', ');
                            }
                            return `${selected.length} Statuses Selected`;
                          }}
                        >
                          <MenuItem value="__all__" sx={{ py: 0.5, fontSize: '0.82rem', fontWeight: 600, color: '#7c3aed' }}>
                            <Checkbox checked={isAllStatusesSelected} size="small" color="secondary" sx={{ py: 0.25, px: 0.5 }} />
                            <ListItemText primary="Select All Statuses" primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 600 }} />
                          </MenuItem>
                          <Divider sx={{ my: 0.5 }} />
                          {FORMATTED_REGISTRATION_STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ py: 0.5, fontSize: '0.82rem' }}>
                              <Checkbox checked={currentStatuses.includes(opt.value)} size="small" color="secondary" sx={{ py: 0.25, px: 0.5 }} />
                              <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '0.82rem' }} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    {/* 2. Package Selection Multi-Select */}
                    <div className="wa-filter-item">
                      <span className="wa-filter-label">Packages:</span>
                      <FormControl size="small" sx={{ minWidth: 180, maxWidth: 260 }}>
                        <Select
                          multiple
                          size="small"
                          disabled={availablePackages.length === 0}
                          value={currentPackageIds}
                          onChange={handlePackageChange}
                          MenuProps={filterSelectMenuProps}
                          sx={{ bgcolor: '#ffffff', fontSize: '0.82rem', height: 36, borderRadius: '8px' }}
                          renderValue={(selected) => {
                            if (availablePackages.length === 0) return <span style={{ color: '#94a3b8' }}>All Packages (None defined)</span>;
                            if (!selected || selected.length === 0 || selected.length === availablePackages.length) return 'All Packages';
                            if (selected.length <= 2) {
                              return selected
                                .map((id) => availablePackages.find((p) => String(p.id) === String(id))?.name || `Package #${id}`)
                                .join(', ');
                            }
                            return `${selected.length} Packages Selected`;
                          }}
                        >
                          {availablePackages.length > 0 && (
                            <MenuItem value="__all__" sx={{ py: 0.5, fontSize: '0.82rem', fontWeight: 600, color: '#7c3aed' }}>
                              <Checkbox checked={isAllPackagesSelected} size="small" color="secondary" sx={{ py: 0.25, px: 0.5 }} />
                              <ListItemText primary="All Packages (Select All)" primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 600 }} />
                            </MenuItem>
                          )}
                          {availablePackages.length > 0 && <Divider sx={{ my: 0.5 }} />}
                          {availablePackages.map((pkg) => (
                            <MenuItem key={pkg.id} value={String(pkg.id)} sx={{ py: 0.5, fontSize: '0.82rem' }}>
                              <Checkbox checked={currentPackageIds.includes(String(pkg.id))} size="small" color="secondary" sx={{ py: 0.25, px: 0.5 }} />
                              <ListItemText
                                primary={pkg.name}
                                secondary={pkg.price ? `₹${Number(pkg.price).toLocaleString('en-IN')}` : null}
                                primaryTypographyProps={{ fontSize: '0.82rem' }}
                                secondaryTypographyProps={{ fontSize: '0.72rem' }}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    {/* 3. Stay Selection (If any) Multi-Select */}
                    {availableStayOptions.length > 0 && (
                      <div className="wa-filter-item">
                        <span className="wa-filter-label">Stay Options:</span>
                        <FormControl size="small" sx={{ minWidth: 170, maxWidth: 250 }}>
                          <Select
                            multiple
                            size="small"
                            value={currentStayIds}
                            onChange={handleStayChange}
                            MenuProps={filterSelectMenuProps}
                            sx={{ bgcolor: '#ffffff', fontSize: '0.82rem', height: 36, borderRadius: '8px' }}
                            renderValue={(selected) => {
                              if (!selected || selected.length === 0 || selected.length === availableStayOptions.length) return 'All Stays';
                              if (selected.length <= 2) {
                                return selected
                                  .map((id) => availableStayOptions.find((s) => String(s.id || s.name) === String(id))?.name || id)
                                  .join(', ');
                              }
                              return `${selected.length} Stays Selected`;
                            }}
                          >
                            <MenuItem value="__all__" sx={{ py: 0.5, fontSize: '0.82rem', fontWeight: 600, color: '#7c3aed' }}>
                              <Checkbox checked={isAllStaysSelected} size="small" color="secondary" sx={{ py: 0.25, px: 0.5 }} />
                              <ListItemText primary="All Stays (Select All)" primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 600 }} />
                            </MenuItem>
                            <Divider sx={{ my: 0.5 }} />
                            {availableStayOptions.map((opt) => {
                              const stayId = String(opt.id || opt.name);
                              return (
                                <MenuItem key={stayId} value={stayId} sx={{ py: 0.5, fontSize: '0.82rem' }}>
                                  <Checkbox checked={currentStayIds.includes(stayId)} size="small" color="secondary" sx={{ py: 0.25, px: 0.5 }} />
                                  <ListItemText
                                    primary={opt.name || opt.id}
                                    secondary={opt.priceAdjust ? `₹${Number(opt.priceAdjust).toLocaleString('en-IN')}` : null}
                                    primaryTypographyProps={{ fontSize: '0.82rem' }}
                                    secondaryTypographyProps={{ fontSize: '0.72rem' }}
                                  />
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </div>
                    )}
                  </div>

                  <div className="wa-trigger-callout purple">
                    <div className="wa-trigger-callout-icon">📅</div>
                    <div className="wa-trigger-callout-body">
                      <span className="wa-trigger-callout-title">Sequence {seqNum}: Automated Milestone Dispatch</span>
                      <p>
                        Templates below will be sent sequentially via WhatsApp exactly <strong>{seq.daysOffset} day(s) {seq.timing}</strong> the <strong>{seq.referenceDate === 'event_start_date' ? 'Event Start Date' : 'Event End Date'}</strong> for participants of <strong>{selectedTrek.name}</strong> matching <strong>Status: [{statusSummary}]</strong>
                        {availablePackages.length > 0 && <> • <strong>Packages: [{packageSummary}]</strong></>}
                        {staySummary && <> • <strong>Stay: [{staySummary}]</strong></>}.
                      </p>
                    </div>
                  </div>

                  <div className="wa-sequence-templates">
                    {seq.templates?.length === 0 ? (
                      <div className="wa-sequence-empty">
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155', mb: 0.5 }}>
                          No templates configured for Sequence {seqNum}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 2, maxWidth: 440, mx: 'auto' }}>
                          Add one or more templates dispatched automatically according to this milestone schedule.
                        </Typography>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          startIcon={<AddCircleOutlineRoundedIcon />}
                          onClick={() => handleAddTemplateToSequence(null, customIdx)}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          Add Template to Sequence {seqNum}
                        </Button>
                      </div>
                    ) : (
                      <>
                        {seq.templates?.map((tpl, idx) =>
                          renderTemplateAccordion(tpl, idx, null, customIdx)
                        )}
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<AddCircleOutlineRoundedIcon />}
                          onClick={() => handleAddTemplateToSequence(null, customIdx)}
                          sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', mt: 0.5 }}
                        >
                          Add Another Template to Sequence {seqNum}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Add Scheduled Sequence Button */}
            <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={handleAddCustomSequence}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: '#cbd5e1',
                  color: '#334155',
                  bgcolor: '#ffffff',
                  px: 3,
                  py: 1,
                  borderRadius: '10px',
                  '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' }
                }}
              >
                + Add Scheduled Sequence (Sequence {3 + (automations.custom_sequences?.length || 0)})
              </Button>
            </div>

            {/* ================= CRM LEAD STATUS AUTOMATION (FOLLOW-UP TRIGGERS) ================= */}
            <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                <div>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                    CRM Follow-Up Automation (Lead Status Triggers)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                    Configure automated WhatsApp templates dispatched to participants when consultants or admins update lead status in CRM.
                  </Typography>
                </div>
              </div>

              {/* 1st Follow Up Done */}
              {renderLeadStatusCard('follow_up_done', 'Follow Up Done', 'Follow Up 1', 'amber', '📞', 'Dispatches automatically when lead status is updated to Follow Up Done.')}

              {/* 2nd Follow Up Done */}
              {renderLeadStatusCard('second_follow_up_done', '2nd Follow Up Done', 'Follow Up 2', 'teal', '🔁', 'Dispatches automatically when lead status is updated to 2nd Follow Up Done.')}

              {/* 3rd Follow Up Done */}
              {renderLeadStatusCard('third_follow_up_done', '3rd Follow Up Done', 'Follow Up 3', 'cyan', '🚀', 'Dispatches automatically when lead status is updated to 3rd Follow Up Done.')}
            </div>
          </div>

          {/* RIGHT COLUMN: Authentic WhatsApp Phone Screen Mockup */}
          <div className="wa-studio-preview-col">
            <div className="wa-phone-card">
              {/* Phone Top Sequence Switcher */}
              <div className="wa-phone-top-controls">
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Live Preview:
                </Typography>
                <Select
                  size="small"
                  value={activePreviewSeq}
                  onChange={(e) => setActivePreviewSeq(e.target.value)}
                  sx={{ height: 28, fontSize: '0.75rem', fontWeight: 600, bgcolor: '#ffffff' }}
                >
                  <ListSubheader sx={{ fontWeight: 700, fontSize: '0.7rem', lineHeight: '24px' }}>
                    Scheduled Lifecycle
                  </ListSubheader>
                  <MenuItem value="sequence1_lead" sx={{ fontSize: '0.75rem' }}>
                    Seq 1: Lead Ingestion ({automations.sequence1_lead?.templates?.filter((t) => t.enabled)?.length || 0})
                  </MenuItem>
                  <MenuItem value="sequence2_booking" sx={{ fontSize: '0.75rem' }}>
                    Seq 2: Booking ({automations.sequence2_booking?.templates?.filter((t) => t.enabled)?.length || 0})
                  </MenuItem>
                  {automations.custom_sequences?.map((cs, idx) => (
                    <MenuItem key={cs.id || idx} value={`custom_${idx}`} sx={{ fontSize: '0.75rem' }}>
                      Seq {idx + 3}: {cs.title} ({cs.templates?.filter((t) => t.enabled)?.length || 0})
                    </MenuItem>
                  ))}
                  <ListSubheader sx={{ fontWeight: 700, fontSize: '0.7rem', lineHeight: '24px' }}>
                    CRM Lead Status Triggers
                  </ListSubheader>
                  <MenuItem value="status_follow_up_done" sx={{ fontSize: '0.75rem' }}>
                    Follow Up Done ({automations.lead_status_automations?.follow_up_done?.templates?.filter((t) => t.enabled)?.length || 0})
                  </MenuItem>
                  <MenuItem value="status_second_follow_up_done" sx={{ fontSize: '0.75rem' }}>
                    2nd Follow Up Done ({automations.lead_status_automations?.second_follow_up_done?.templates?.filter((t) => t.enabled)?.length || 0})
                  </MenuItem>
                  <MenuItem value="status_third_follow_up_done" sx={{ fontSize: '0.75rem' }}>
                    3rd Follow Up Done ({automations.lead_status_automations?.third_follow_up_done?.templates?.filter((t) => t.enabled)?.length || 0})
                  </MenuItem>
                </Select>
              </div>

              {/* Phone Chassis */}
              <div className="wa-phone-screen">
                <div className="wa-phone-device">
                  {/* Status Bar */}
                  <div className="wa-phone-status-bar">
                    <span>9:41</span>
                    <span style={{ display: 'flex', gap: 4 }}>📶 🔋</span>
                  </div>

                  {/* WhatsApp App Header */}
                  <div className="wa-phone-wa-header">
                    <div className="wa-phone-wa-contact">
                      <div className="wa-phone-avatar">🏔️</div>
                      <div className="wa-phone-contact-info">
                        <h5>
                          Invincible NGO <VerifiedRoundedIcon sx={{ fontSize: 13, color: '#22c55e' }} />
                        </h5>
                        <span>Online</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: 0.9 }}>
                      <VideocamRoundedIcon sx={{ fontSize: 18 }} />
                      <CallRoundedIcon sx={{ fontSize: 16 }} />
                      <MoreVertRoundedIcon sx={{ fontSize: 18 }} />
                    </div>
                  </div>

                  {/* WhatsApp Chat Canvas */}
                  <div className="wa-phone-chat-canvas">
                    <div className="wa-phone-date-pill">Today</div>
                    <div className="wa-phone-encryption-note">
                      🔒 Messages and calls are end-to-end encrypted. No one outside of this chat can read or listen to them.
                    </div>

                    {previewTemplates.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#64748b' }}>
                        <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block' }}>
                          No enabled templates in this sequence.
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                          Add or enable a template on the left to see live mockup preview.
                        </Typography>
                      </div>
                    ) : (
                      previewTemplates.map((tpl, pIdx) => {
                        const meta = templateMap.get(tpl.id);
                        const isCarousel = Boolean(
                          tpl.isCarousel ||
                          meta?.isCarousel ||
                          String(tpl.headerFormat || meta?.headerFormat || '').toUpperCase() === 'CAROUSEL'
                        );
                        const headerFormat = isCarousel ? 'CAROUSEL' : (tpl.headerFormat || meta?.headerFormat || '').toUpperCase();
                        const isImage = headerFormat === 'IMAGE';
                        const isDoc = headerFormat === 'DOCUMENT';
                        const effectiveCards = (Array.isArray(tpl.carouselCards) && tpl.carouselCards.length > 0)
                          ? tpl.carouselCards
                          : (Array.isArray(meta?.carouselCards) ? meta.carouselCards : []);

                        if (isCarousel) {
                          return (
                            <div key={pIdx} className="wa-chat-carousel-container">
                              {/* Header Intro Body Message */}
                              <div className="wa-chat-bubble wa-chat-bubble-carousel-intro">
                                <div className="wa-chat-bubble-text">
                                  {renderWhatsAppFormattedText(tpl.body, selectedTrek?.name, 'Rahul Sharma')}
                                </div>
                                <div className="wa-chat-bubble-meta">
                                  <span>12:{30 + pIdx} PM</span>
                                  <DoneAllRoundedIcon sx={{ fontSize: 13, color: '#53bdeb' }} />
                                </div>
                              </div>

                              {/* Horizontal Carousel Cards */}
                              <div className="wa-chat-carousel-scroll">
                                {effectiveCards.map((card, cIdx) => {
                                  const configuredUrl = tpl.carouselData?.[cIdx]?.mediaUrl || '';
                                  const defaultCardUrl = card.media_url || card.mediaUrl || '';
                                  const activeCardImg = configuredUrl || defaultCardUrl;

                                  return (
                                    <div key={cIdx} className="wa-chat-carousel-card">
                                      {activeCardImg ? (
                                        <img
                                          src={activeCardImg}
                                          alt={`Card ${cIdx + 1}`}
                                          className="wa-chat-carousel-card-img"
                                        />
                                      ) : (
                                        <div className="wa-chat-carousel-card-img-placeholder">
                                          <ImageRoundedIcon sx={{ fontSize: 28, color: '#cbd5e1' }} />
                                        </div>
                                      )}
                                      <div className="wa-chat-carousel-card-content">
                                        <div className="wa-chat-carousel-card-text">
                                          {renderWhatsAppFormattedText(card.body_text || card.body || '', selectedTrek?.name, 'Rahul')}
                                        </div>
                                        {Array.isArray(card.buttons) && card.buttons.length > 0 && (
                                          <div className="wa-chat-carousel-card-buttons">
                                            {card.buttons.map((btn, bIdx) => (
                                              <div key={bIdx} className="wa-chat-carousel-card-btn">
                                                <span>🔗 {btn.text || 'Open Link'}</span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="wa-chat-carousel-hint">
                                ⇄ Swipe horizontally to preview {effectiveCards.length} cards
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div key={pIdx} className="wa-chat-bubble">
                            {/* Header Image if available */}
                            {isImage && (tpl.mediaUrl || tpl.sampleMediaUrl) && (
                              <img
                                src={tpl.mediaUrl || tpl.sampleMediaUrl}
                                alt="WhatsApp Attachment"
                                className="wa-chat-bubble-image"
                              />
                            )}

                            {/* Header PDF card if available */}
                            {isDoc && (
                              <div className="wa-chat-bubble-doc">
                                <div className="wa-chat-bubble-doc-icon">
                                  <InsertDriveFileRoundedIcon fontSize="small" />
                                </div>
                                <div className="wa-chat-bubble-doc-info">
                                  <div className="wa-chat-bubble-doc-name">
                                    {selectedTrek?.name || 'Trek'}_Brochure.pdf
                                  </div>
                                  <div className="wa-chat-bubble-doc-meta">PDF • 2.4 MB</div>
                                </div>
                              </div>
                            )}

                            {/* Body text with WhatsApp bolding */}
                            <div className="wa-chat-bubble-text">
                              {renderWhatsAppFormattedText(tpl.body, selectedTrek?.name, 'Rahul Sharma')}
                            </div>

                            {/* Bubble footer with time & double blue ticks */}
                            <div className="wa-chat-bubble-meta">
                              <span>12:{30 + pIdx} PM</span>
                              <DoneAllRoundedIcon sx={{ fontSize: 13, color: '#53bdeb' }} />
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* WhatsApp Bottom Input Bar Mockup */}
                  <div className="wa-phone-input-bar">
                    <div className="wa-phone-input-field">
                      <SentimentSatisfiedAltRoundedIcon sx={{ fontSize: 18, color: '#8696a0' }} />
                      <span>Message</span>
                      <div style={{ display: 'flex', gap: 6, color: '#8696a0' }}>
                        <AttachFileRoundedIcon sx={{ fontSize: 16 }} />
                        <CameraAltRoundedIcon sx={{ fontSize: 16 }} />
                      </div>
                    </div>
                    <div className="wa-phone-mic-btn">
                      <MicRoundedIcon sx={{ fontSize: 16 }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '4rem 1.5rem', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
          <WhatsAppIcon sx={{ fontSize: 56, color: '#22c55e', mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
            No Trek Selected
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 460, mx: 'auto' }}>
            Please search and pick a trek above to view, configure, and orchestrate its multi-sequence WhatsApp messaging lifecycle.
          </Typography>
        </div>
      )}

      {/* Sticky Bottom Actions Footer */}
      {selectedTrek && (
        <div className="wa-action-footer">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleRoundedIcon />}
              disabled={saving || !canManage}
              onClick={handleSaveAutomation}
              sx={{
                bgcolor: '#ea580c',
                '&:hover': { bgcolor: '#c2410c' },
                textTransform: 'none',
                fontWeight: 700,
                px: 3.5
              }}
            >
              {saving ? 'Saving Changes...' : 'Save WhatsApp Automation'}
            </Button>

            {isDirty ? (
              <Typography variant="body2" sx={{ color: '#d97706', fontWeight: 600 }}>
                ⚠️ You have unsaved changes
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleRoundedIcon fontSize="small" /> All changes saved
              </Typography>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.65rem' }}>
            <Button
              variant="outlined"
              startIcon={<SendRoundedIcon />}
              disabled={!canManage}
              onClick={() => {
                setTestSequenceKey(activePreviewSeq);
                setTestSendOpen(true);
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Test Send
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyRoundedIcon />}
              disabled={!canManage}
              onClick={() => {
                setTargetTrekIds([]);
                setCopyModalOpen(true);
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Copy to Other Treks
            </Button>
          </div>
        </div>
      )}

      {/* Test Send Dialog */}
      <Dialog open={testSendOpen} onClose={() => setTestSendOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Test WhatsApp Dispatch</span>
          <IconButton size="small" onClick={() => setTestSendOpen(false)}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Simulate an automated trigger for <strong>{selectedTrek?.name}</strong> by sending templates to a test WhatsApp number.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', mb: 0.5, display: 'block' }}>
                Select Sequence to Test
              </Typography>
              <Select
                size="small"
                fullWidth
                value={testSequenceKey}
                onChange={(e) => setTestSequenceKey(e.target.value)}
              >
                <ListSubheader sx={{ fontWeight: 700, fontSize: '0.75rem', lineHeight: '30px' }}>
                  Lifecycle Sequences
                </ListSubheader>
                <MenuItem value="sequence1_lead">
                  Sequence 1: Lead Ingestion ({automations.sequence1_lead?.templates?.filter((t) => t.enabled)?.length || 0} active)
                </MenuItem>
                <MenuItem value="sequence2_booking">
                  Sequence 2: Booking Confirmed ({automations.sequence2_booking?.templates?.filter((t) => t.enabled)?.length || 0} active)
                </MenuItem>
                {automations.custom_sequences?.map((cs, idx) => (
                  <MenuItem key={cs.id || idx} value={`custom_${idx}`}>
                    Sequence {idx + 3}: {cs.title} ({cs.templates?.filter((t) => t.enabled)?.length || 0} active)
                  </MenuItem>
                ))}
                <ListSubheader sx={{ fontWeight: 700, fontSize: '0.75rem', lineHeight: '30px' }}>
                  CRM Lead Status Triggers
                </ListSubheader>
                <MenuItem value="status_follow_up_done">
                  Follow Up Done ({automations.lead_status_automations?.follow_up_done?.templates?.filter((t) => t.enabled)?.length || 0} active)
                </MenuItem>
                <MenuItem value="status_second_follow_up_done">
                  2nd Follow Up Done ({automations.lead_status_automations?.second_follow_up_done?.templates?.filter((t) => t.enabled)?.length || 0} active)
                </MenuItem>
                <MenuItem value="status_third_follow_up_done">
                  3rd Follow Up Done ({automations.lead_status_automations?.third_follow_up_done?.templates?.filter((t) => t.enabled)?.length || 0} active)
                </MenuItem>
              </Select>
            </div>

            <TextField
              label="Recipient Mobile Number"
              placeholder="e.g. 9876543210"
              size="small"
              fullWidth
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              helperText="10-digit Indian mobile number"
            />
            <TextField
              label="Recipient Name"
              size="small"
              fullWidth
              value={testName}
              onChange={(e) => setTestName(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTestSendOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={testSending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
            disabled={testSending || !testPhone}
            onClick={handleTestSend}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {testSending ? 'Sending...' : 'Send Test WhatsApp Sequence'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Copy Dialog */}
      <Dialog open={copyModalOpen} onClose={() => setCopyModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Copy Lifecycle Automation</span>
          <IconButton size="small" onClick={() => setCopyModalOpen(false)}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Duplicate the complete multi-sequence lifecycle configured for <strong>{selectedTrek?.name}</strong> to other treks.
          </Typography>

          <TextField
            size="small"
            fullWidth
            placeholder="Search target treks..."
            value={copySearch}
            onChange={(e) => setCopySearch(e.target.value)}
            sx={{ mb: 1.5 }}
          />

          <Box sx={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', p: 1 }}>
            {treks
              .filter((t) => String(t.id) !== String(selectedTrek?.id))
              .filter((t) => t.name.toLowerCase().includes(copySearch.toLowerCase()))
              .map((trek) => {
                const isChecked = targetTrekIds.includes(trek.id);
                return (
                  <Box
                    key={trek.id}
                    onClick={() => {
                      setTargetTrekIds((prev) =>
                        isChecked ? prev.filter((id) => id !== trek.id) : [...prev, trek.id]
                      );
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      cursor: 'pointer',
                      borderRadius: '6px',
                      bgcolor: isChecked ? '#f0fdf4' : 'transparent',
                      '&:hover': { bgcolor: isChecked ? '#dcfce7' : '#f8fafc' }
                    }}
                  >
                    <div>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                        {trek.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Code: {trek.code || 'N/A'} • {getCategoryLabel(trek.category)}
                      </Typography>
                    </div>
                    <Chip
                      size="small"
                      label={isChecked ? 'Selected' : 'Select'}
                      color={isChecked ? 'success' : 'default'}
                      sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600 }}
                    />
                  </Box>
                );
              })}
          </Box>
          <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block' }}>
            {targetTrekIds.length} trek(s) selected
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCopyModalOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={copying ? <CircularProgress size={16} color="inherit" /> : <ContentCopyRoundedIcon />}
            disabled={copying || targetTrekIds.length === 0}
            onClick={handleBulkCopy}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {copying ? 'Applying...' : `Apply to ${targetTrekIds.length} Trek(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
